import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import {
  Home,
  Building,
  DollarSign,
  Users,
  BarChart3,
  MessageSquare,
  Settings,
  CalendarCog,
  FileText,
  FolderOpen,
  CreditCard,
  Brain,
  Globe,
} from "lucide-react";

const menuItems = [
  { title: "Dashboard", path: "/owner-dashboard", icon: Home },
  { title: "Schools", path: "/owner/schools", icon: Building },
  { title: "Resources", path: "/owner/resources", icon: FolderOpen },
  { title: "System Configuration", path: "/owner/system-config", icon: CalendarCog },
  { title: "Landing Page", path: "/owner/landing-settings", icon: Globe },
  { title: "School Website", path: "/owner/school-website", icon: Globe },
  { title: "AI Control", path: "/owner/ai-control", icon: Brain },
  { title: "Subscriptions", path: "/owner/subscriptions", icon: FileText },
  { title: "Payments & Billing", path: "/owner/payments", icon: DollarSign },
  { title: "Payment Gateways", path: "/owner/payment-gateways", icon: CreditCard },
  { title: "Users & Roles", path: "/owner/users", icon: Users },
  { title: "Analytics", path: "/owner/analytics", icon: BarChart3 },
  { title: "Support & Tickets", path: "/owner/support", icon: MessageSquare },
  { title: "Settings", path: "/owner/settings", icon: Settings },
];


// ...other imports

export interface OwnerSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  userRole: string;
}

// ...rest of OwnerSidebar code
const OwnerSidebar = ({ userRole }: OwnerSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    setActivePath(path);
    navigate(path);
  };

  return (
  <Sidebar className="bg-white dark:bg-gray-900 border-r-0">
      <SidebarContent className="p-2 sm:p-4 bg-white dark:bg-gray-900">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
            {userRole === "owner" && <Building className="h-3 w-3" />}
            {userRole ? `${userRole.charAt(0).toUpperCase() + userRole.slice(1)} Menu` : "Menu"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    isActive={activePath === item.path}
                    className="w-full justify-start cursor-pointer p-2 sm:p-3 rounded-lg transition-all duration-200 touch-manipulation text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 data-[active=true]:bg-blue-100 dark:data-[active=true]:bg-blue-900 data-[active=true]:text-blue-700 dark:data-[active=true]:text-blue-300"
                    onClick={() => handleNavigation(item.path)}
                  >
                    <item.icon className="mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="text-sm sm:text-base truncate">{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default OwnerSidebar;